import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Message } from "primereact/message";
import { dateToDay, dayToDate } from "../../../../helpers/CertificateDate";

// The API's refusals of a picked day, and their messages.
const ERROR_MESSAGES = {
  COMPLETION_DATE_IN_FUTURE: "completion_date_in_future",
  COMPLETION_DATE_BEFORE_ENROLMENT: "completion_date_before_enrolment",
};

// Mark As Completed asks for the completion date: today by default, not in the future and not before the learner's
// enrolment. The days come from the pending list (earliestCompletionDate and
// latestCompletionDate, days in the enrolment's time zone, which is also the
// calendar of the certificate's date); the API checks them again.
export const MarkCompletedDialog = ({
  assignment,
  error,
  onHide,
  onSubmit,
  requesting,
}) => {
  const intl = useIntl();
  const assignmentId = assignment?.id;
  const earliest = assignment?.earliestCompletionDate;
  const latest = assignment?.latestCompletionDate;
  const minDate = useMemo(() => dayToDate(earliest), [earliest]);
  const maxDate = useMemo(
    () => dayToDate(latest) || dayjs().startOf("day").toDate(),
    [latest]
  );
  const [date, setDate] = useState(maxDate);

  // Each opening starts again from today.
  useEffect(() => {
    setDate(maxDate);
  }, [assignmentId, maxDate]);

  const errorCode = error?.response;
  const errorMessage =
    (error &&
      intl.formatMessage(
        { id: ERROR_MESSAGES[errorCode] || "something_else_wrong" },
        {
          date: error.values ? dayjs(error.values).format("DD MMM YYYY") : "",
        }
      )) ||
    null;

  const footer = (
    <div className="flex flex-column gap-3">
      {(errorMessage && (
        <Message severity="error" text={errorMessage} className="w-full" />
      )) ||
        null}
      <div className="flex justify-content-end gap-2">
        <Button
          type="button"
          label={intl.formatMessage({ id: "cancel" })}
          outlined
          onClick={onHide}
        />
        <Button
          type="button"
          label={intl.formatMessage({ id: "mark_as_completed" })}
          disabled={!date || requesting}
          loading={requesting}
          onClick={() => onSubmit(dateToDay(date))}
        />
      </div>
    </div>
  );

  return (
    <Dialog
      visible={!!assignment}
      onHide={onHide}
      header={intl.formatMessage({ id: "mark_completed_dialog_title" })}
      footer={footer}
      style={{ width: "28rem" }}
      breakpoints={{ "640px": "calc(100vw - 2rem)" }}
    >
      {(assignment && (
        <div className="flex flex-column gap-2">
          <div className="font-semibold">{assignment.learningObjectName}</div>
          <label htmlFor="markCompletedDate" className="edexlms-field-label">
            {intl.formatMessage({ id: "completion_date" })}
          </label>
          <Calendar
            inputId="markCompletedDate"
            value={date}
            onChange={(e) => setDate(e.value || null)}
            minDate={minDate}
            maxDate={maxDate}
            dateFormat="dd M yy"
            showIcon
            className="w-full"
          />
          <small className="text-muted">
            {intl.formatMessage({ id: "completion_date_hint" })}
          </small>
        </div>
      )) ||
        null}
    </Dialog>
  );
};

MarkCompletedDialog.propTypes = {
  assignment: PropTypes.shape({
    id: PropTypes.string,
    learningObjectName: PropTypes.string,
    earliestCompletionDate: PropTypes.string,
    latestCompletionDate: PropTypes.string,
  }),
  error: PropTypes.object,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  requesting: PropTypes.bool,
};

export default MarkCompletedDialog;
